/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Row,
  Space,
  Typography,
} from 'antd';
import styles from '../styles.module.css';
import {
  formatWithThousandSeparator,
  parseFormattedNumber,
} from '../../../../../services/utils/commonUtils';

const { Text, Title } = Typography;
function EnergyReadingsCard({
  t,
  form,
  title,
  subtitle,
  sections,
  cardType,
  selectedOption,
  onOptionChange,
  broughtForwards,
  getValidationRules,
}) {
  const isExport = cardType === 'bulkExport' || cardType === 'activeExport';
  const isMaximumDemand = (section) => section.type === 'maximumDemand';

  const type = selectedOption || 'Range';
  const isRange = type === 'Range';

  return (
    <Row gutter={16} className={styles.padding20}>
      {/* Header */}
      <Col span={24}>
        <Row justify="space-between" align="middle">
          <div className={styles.headerContent}>
            <Title level={5} className={styles.headerTitle}>
              {title}
            </Title>
            <Text className={styles.headerSubtitle}>{subtitle}</Text>
          </div>

          <Space.Compact className={styles.marginBottom10}>
            <Button
              className={`${styles.toggleButton} ${isRange ? styles.activeButton : ''}`}
              onClick={() => onOptionChange(cardType, 'Range')}
            >
              {t('label_readinds')}
            </Button>
            <Button
              className={`${styles.toggleButton} ${!isRange ? styles.activeButton : ''}`}
              onClick={() => onOptionChange(cardType, 'Number')}
            >
              {t('label_total_units')}
            </Button>
          </Space.Compact>
        </Row>
      </Col>

      {/* Sections */}
      <Col span={24} className={styles.marginTop20}>
        {sections.map((section) => {
          return (
            <div key={section.type} className={styles.energySection}>
              <div className={styles.energySectionHeader}>
                <section.icon className={styles.energySectionIcon} />
                <Text className={styles.energySectionTitle}>
                  {t(section.title)}
                  <span className={styles.registerCode}>({section.code})</span>
                </Text>
              </div>

              {/* Fields */}
              {isMaximumDemand(section) ? (
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Text className={styles.fieldLabel}>
                      {t('label_number_of_units')}
                    </Text>
                    <Form.Item
                      className={styles.formItemNew}
                      name={[cardType, section.type, 'readings']}
                      rules={[
                        {
                          required: true,
                          message: t(
                            'label_please_enter_maximum_demand_reading',
                          ),
                        },
                        {
                          type: 'number',
                          max: 999999,
                          message: t(
                            'label_maximim_demand_should_less_than_million',
                          ),
                        },
                        {
                          type: 'number',
                          min: 0,
                          message: t(
                            'label_meter_reading_positive_number_error',
                          ),
                        },
                      ]}
                    >
                      <InputNumber
                        className={styles.input}
                        min={0}
                        placeholder="0"
                        formatter={formatWithThousandSeparator}
                        parser={parseFormattedNumber}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ) : isRange ? (
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Text className={styles.fieldLabel}>
                      {t('label_previous_meter_reading')}
                    </Text>
                    <Form.Item
                      className={styles.formItemNew}
                      name={[cardType, section.type, 'previousReading']}
                      dependencies={[
                        [cardType, section.type, 'currentReading'],
                      ]}
                      rules={[
                        ...getValidationRules(
                          'previousReading',
                          'reading',
                          [cardType, section.type, 'currentReading'],
                          t,
                        ),
                        {
                          type: 'number',
                          min: 0,
                          message: t(
                            'label_meter_reading_positive_number_error',
                          ),
                        },
                      ]}
                    >
                      <InputNumber
                        className={styles.input}
                        min={0}
                        placeholder="0"
                        formatter={formatWithThousandSeparator}
                        parser={parseFormattedNumber}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Text className={styles.fieldLabel}>
                      {t('label_current_meter_reading')}
                    </Text>
                    <Form.Item
                      className={styles.formItemNew}
                      name={[cardType, section.type, 'currentReading']}
                      dependencies={[
                        [cardType, section.type, 'previousReading'],
                      ]}
                      rules={[
                        ...getValidationRules(
                          'currentReading',
                          'reading',
                          [cardType, section.type, 'previousReading'],
                          t,
                        ),
                        {
                          type: 'number',
                          min: 0,
                          message: t(
                            'label_meter_reading_positive_number_error',
                          ),
                        },
                      ]}
                    >
                      <InputNumber
                        className={styles.input}
                        min={0}
                        placeholder="0"
                        formatter={formatWithThousandSeparator}
                        parser={parseFormattedNumber}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ) : (
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Text className={styles.fieldLabel}>
                      {t('label_number_of_units')}
                    </Text>
                    <Form.Item
                      className={styles.formItemNew}
                      name={[cardType, section.type, 'numOfUnits']}
                      rules={[
                        {
                          required: true,
                          message: t('label_please_enter_number_of_units'),
                        },
                        {
                          type: 'number',
                          max: 999999,
                          message: t(
                            'label_number_of_units_should_be_less_than_million',
                          ),
                        },
                        {
                          type: 'number',
                          min: 0,
                          message: t(
                            'label_meter_reading_positive_number_error',
                          ),
                        },
                      ]}
                    >
                      <InputNumber
                        className={styles.input}
                        min={0}
                        placeholder="0"
                        formatter={(value) =>
                          formatWithThousandSeparator(value)
                        }
                        parser={(value) => parseFormattedNumber(value)}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </div>
          );
        })}
      </Col>
      {broughtForwards && isExport && (
        <Col xs={24} md={12} className={styles.marginTop20}>
          <Text className={styles.fieldLabel}>
            {t('label_brought_forward_units')}
          </Text>
          <Form.Item
            className={styles.formItemNew}
            name={[cardType, 'broughtForward']}
            rules={[
              {
                required: true,
                message: t('label_please_enter_brought_forward_units'),
              },
              {
                type: 'number',
                max: 999999,
                message: t('label_number_of_units_should_be_less_than_million'),
              },
              {
                type: 'number',
                min: 0,
                message: t('label_meter_reading_positive_number_error'),
              },
            ]}
          >
            <InputNumber
              className={styles.input}
              min={0}
              placeholder={t('label_enter_brought_forward_units')}
              formatter={formatWithThousandSeparator}
              parser={parseFormattedNumber}
            />
          </Form.Item>
        </Col>
      )}
    </Row>
  );
}

export default EnergyReadingsCard;
